import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment-timezone';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

// =============================================================================
// InlineRangeCalendar — dual-month inline range picker. Pure moment + raw HTML
// (no AntD RangePicker, no third-party calendar lib).
//
// Field-edit model:
//   - Parent passes `editingField` ('start' | 'end').
//   - Click a cell → update the editingField only, fire onChange, then ask
//     parent to flip editingField (`onEditingFieldChange`) so the next
//     cell click edits the OTHER side. After both fields touched in one
//     pass, parent can decide to close the popover.
//   - Hovering over a cell shows a tentative-range preview against the
//     opposite side of the active field.
// =============================================================================

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const buildMonthGrid = (monthMoment) => {
  const firstOfMonth = monthMoment.clone().startOf('month');
  const startWeekday = firstOfMonth.day();
  const gridStart = firstOfMonth.clone().subtract(startWeekday, 'days');
  const days = [];
  for (let i = 0; i < 42; i += 1) {
    days.push(gridStart.clone().add(i, 'days'));
  }
  return days;
};

const sortDates = (a, b) => (a.isBefore(b) ? [a, b] : [b, a]);

const navBtnStyle = {
  appearance: 'none',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
};

const InlineRangeCalendar = ({
  value,
  onChange,
  defaultMonth,
  disabledDate,
  editingField = 'start',
  onEditingFieldChange,
  numberOfMonths = 2,
}) => {
  const [hoverDate, setHoverDate] = useState(null);

  const initialMonth = useMemo(() => {
    if (value && value[0]) return value[0].clone().startOf('month');
    if (defaultMonth) return moment(defaultMonth).clone().startOf('month');
    return moment().startOf('month');
  }, [value, defaultMonth]);
  const [leftMonth, setLeftMonth] = useState(initialMonth);
  const rightMonth = leftMonth.clone().add(1, 'month');

  // Auto-teleport: when the active field's date isn't visible in either
  // panel, jump the calendar so it is. Triggered by:
  //   - editingField change (user clicked the other input)
  //   - value change (preset click, typed-input commit, etc.)
  // If the active date is already on screen we leave the calendar where
  // the user last navigated.
  const valStartStamp = value && value[0] && value[0].valueOf ? value[0].valueOf() : null;
  const valEndStamp = value && value[1] && value[1].valueOf ? value[1].valueOf() : null;
  useEffect(() => {
    const target = editingField === 'end' ? value && value[1] : value && value[0];
    if (!target) return;
    const targetMonth = moment(target).startOf('month');
    const inLeft = targetMonth.isSame(leftMonth, 'month');
    const inRight = targetMonth.isSame(leftMonth.clone().add(1, 'month'), 'month');
    if (!inLeft && !inRight) {
      setLeftMonth(targetMonth);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingField, valStartStamp, valEndStamp]);

  const leftDays = useMemo(() => buildMonthGrid(leftMonth), [leftMonth]);
  const rightDays = useMemo(() => buildMonthGrid(rightMonth), [rightMonth]);

  const handleCellClick = (date) => {
    if (disabledDate && disabledDate(date)) return;
    const currentStart = (value && value[0]) || null;
    const currentEnd = (value && value[1]) || null;

    if (editingField === 'end') {
      // Only updating end. Auto-swap if user picks before current start.
      const start = currentStart || date.clone();
      let end = date.clone();
      let nextStart = start;
      if (end.isBefore(nextStart, 'day')) {
        [nextStart, end] = sortDates(nextStart, end);
      }
      if (onChange) onChange([nextStart.startOf('day'), end.endOf('day')]);
      if (onEditingFieldChange) onEditingFieldChange('start');
    } else {
      // Updating start (default). Auto-swap if user picks after current end.
      let start = date.clone();
      const end = currentEnd || date.clone();
      let nextEnd = end;
      if (start.isAfter(nextEnd, 'day')) {
        [start, nextEnd] = sortDates(start, nextEnd);
      }
      if (onChange) onChange([start.startOf('day'), nextEnd.endOf('day')]);
      if (onEditingFieldChange) onEditingFieldChange('end');
    }
  };

  // Visual range — what cells are shown as in-range. While hovering with
  // an active field, show a live preview against the opposite endpoint so
  // the user can see what the new range would be.
  const visualRange = useMemo(() => {
    const start = value && value[0];
    const end = value && value[1];
    if (hoverDate) {
      if (editingField === 'end' && start) {
        return sortDates(start, hoverDate);
      }
      if (editingField === 'start' && end) {
        return sortDates(hoverDate, end);
      }
    }
    if (start && end) return [start, end];
    return [null, null];
  }, [hoverDate, editingField, value]);

  const isInRange = (date) => {
    const [start, end] = visualRange;
    if (!start || !end) return false;
    return date.isBetween(start, end, 'day', '[]');
  };
  const isRangeStart = (date) => {
    const [start] = visualRange;
    return start && date.isSame(start, 'day');
  };
  const isRangeEnd = (date) => {
    const [, end] = visualRange;
    return end && date.isSame(end, 'day');
  };

  const renderCell = (date, monthMoment) => {
    const isCurrentMonth = date.isSame(monthMoment, 'month');
    const isDisabled = disabledDate ? disabledDate(date) : false;
    // Off-month cells are visual-only padding — they're never highlighted
    // (otherwise a range that crosses months looks like 4 selected dates,
    // duplicating the same days across both panels) and never clickable.
    const inRange = isCurrentMonth && isInRange(date);
    const start = isCurrentMonth && isRangeStart(date);
    const end = isCurrentMonth && isRangeEnd(date);
    const isToday = date.isSame(moment(), 'day');
    const blocked = isDisabled || !isCurrentMonth;

    let bg = 'transparent';
    let color = isCurrentMonth ? '#1f2937' : '#cbd5e1';
    let fontWeight = 400;

    if (blocked) {
      color = '#cbd5e1';
    } else if (start || end) {
      bg = '#1890ff';
      color = '#fff';
      fontWeight = 600;
    } else if (inRange) {
      bg = 'rgba(24, 144, 255, 0.10)';
      color = '#1890ff';
    }

    return (
      <button
        key={date.format('YYYY-MM-DD')}
        type="button"
        disabled={blocked}
        onClick={blocked ? undefined : () => handleCellClick(date)}
        onMouseEnter={blocked ? undefined : () => setHoverDate(date.clone())}
        onMouseLeave={blocked ? undefined : () => setHoverDate(null)}
        style={{
          appearance: 'none',
          border: 'none',
          width: 32,
          height: 32,
          borderRadius: 6,
          fontSize: 12,
          fontVariantNumeric: 'tabular-nums',
          background: bg,
          color,
          fontWeight,
          cursor: blocked ? 'default' : 'pointer',
          opacity: blocked ? 0.6 : 1,
          padding: 0,
          margin: 0,
          fontFamily: 'inherit',
          position: 'relative',
          outline:
            isToday && isCurrentMonth && !start && !end ? '1px solid #cbd5e1' : 'none',
          outlineOffset: -2,
        }}
      >
        {date.date()}
      </button>
    );
  };

  const renderMonth = (monthMoment, days) => (
    <div className="ds-cal-month" style={{ flex: 1, padding: '0 8px' }}>
      <div
        className="ds-cal-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 32px)',
          gap: 2,
          justifyContent: 'center',
        }}
      >
        {WEEKDAYS.map((d) => (
          <div
            key={d}
            style={{
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#94a3b8',
              fontWeight: 600,
              textAlign: 'center',
              padding: '6px 0',
            }}
          >
            {d}
          </div>
        ))}
        {days.map((d) => renderCell(d, monthMoment))}
      </div>
    </div>
  );

  return (
    <div
      className="ds-cal"
      style={{
        padding: '12px 8px',
        fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
      }}
    >
      <div
        className="ds-cal-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px 8px',
        }}
      >
        <div
          style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
        >
          <button
            type="button"
            onClick={() => setLeftMonth((m) => m.clone().subtract(1, 'year'))}
            style={navBtnStyle}
            aria-label="Previous year"
          >
            <ChevronsLeft size={16} color="#374151" />
          </button>
          <button
            type="button"
            onClick={() => setLeftMonth((m) => m.clone().subtract(1, 'month'))}
            style={navBtnStyle}
            aria-label="Previous month"
          >
            <ChevronLeft size={16} color="#374151" />
          </button>
        </div>
        <span
          style={{
            flex: 1,
            textAlign: 'center',
            fontSize: 13,
            fontWeight: 600,
            color: '#1f2937',
            letterSpacing: '-0.01em',
          }}
        >
          {leftMonth.format('MMMM YYYY')}
        </span>
        {numberOfMonths > 1 && (
          <span
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 13,
              fontWeight: 600,
              color: '#1f2937',
              letterSpacing: '-0.01em',
            }}
          >
            {rightMonth.format('MMMM YYYY')}
          </span>
        )}
        <div
          style={{ display: 'inline-flex', alignItems: 'center', gap: 2, flexShrink: 0 }}
        >
          <button
            type="button"
            onClick={() => setLeftMonth((m) => m.clone().add(1, 'month'))}
            style={navBtnStyle}
            aria-label="Next month"
          >
            <ChevronRight size={16} color="#374151" />
          </button>
          <button
            type="button"
            onClick={() => setLeftMonth((m) => m.clone().add(1, 'year'))}
            style={navBtnStyle}
            aria-label="Next year"
          >
            <ChevronsRight size={16} color="#374151" />
          </button>
        </div>
      </div>
      <div className="ds-cal-months" style={{ display: 'flex', gap: 8 }}>
        {renderMonth(leftMonth, leftDays)}
        {numberOfMonths > 1 && renderMonth(rightMonth, rightDays)}
      </div>
    </div>
  );
};

export default InlineRangeCalendar;
